import { call, put, race, select, take, takeEvery } from "redux-saga/effects";
import { v4 as uuidv4 } from "uuid";

import {
  SideLoadedBusinessAction,
  SideLoadedDataMappingAction,
  SideLoadedDataSetAction,
  saveSideLoadedDataSetMappingFailure,
  saveSideLoadedDataSetMappingSuccess,
  uploadSideLoadedDataSetFailure,
  uploadSideLoadedDataSetSuccess,
} from "actions/sideLoadedData";

import {
  SIDELOADED_BUSINESS_SET,
  SIDELOADED_DATA_MAPPING,
} from "constants/actionTypes";

import { closeModal } from "lib/navigation";
import toast from "lib/toast";

import { refreshJWTAndRetry } from "sagas/api";

import { createUploadFileChannel } from "./createUploadFileChannel";

const UPLOAD_URL = "/v2/sideloaded-data-sets/";

// Action creators replace options wholesale, so a correlated dispatch carries these.
const DEFAULT_ACTION_OPTIONS = {
  suppressToast: false,
  suppressErrorToast: false,
  suppressSuccessToast: false,
  changeReason: null,
  saleyardName: null,
};

// These run concurrently and the result actions are indistinguishable, so tag
// this dispatch and wait for the tag back.
function* takeOwnResult(
  correlationId: string,
  success: string,
  failure: string,
) {
  const outcome = yield race({
    success: take(
      (a: { type: string; options?: { correlationId?: string } }) =>
        a.type === success && a.options?.correlationId === correlationId,
    ),
    failure: take(
      (a: { type: string; options?: { correlationId?: string } }) =>
        a.type === failure && a.options?.correlationId === correlationId,
    ),
  });

  return outcome;
}

// The readable reason out of a DRF rejection.
function getErrorMessage(response: unknown, fallback: string): string {
  const fieldErrors = (response as Record<string, string[] | string>) || {};
  // DRF puts a PermissionDenied's reason in `detail`, field errors under the field.
  const firstError = (
    fieldErrors.detail
      ? [fieldErrors.detail]
      : Object.values(fieldErrors).flat()
  )[0];
  if (typeof firstError === "string") {
    return firstError;
  }
  return fallback;
}

// The upload is a bare XHR, so the template has to exist server-side first or it
// races the offline queue.
export function* createTemplate({ templateName, entries, sourceColumns }) {
  const correlationId = uuidv4();
  yield put(
    SideLoadedDataMappingAction.create(
      {
        name: templateName,
        entries,
        sourceColumns,
      },
      { ...DEFAULT_ACTION_OPTIONS, correlationId },
    ),
  );
  const { success } = yield call(
    takeOwnResult,
    correlationId,
    SIDELOADED_DATA_MAPPING.CREATE.SUCCESS,
    SIDELOADED_DATA_MAPPING.CREATE.FAILURE,
  );

  return success?.payload?.id || null;
}

// Waited on for the same reason as createTemplate.
export function* updateTemplate({
  templateId,
  templateName,
  entries,
  sourceColumns,
}) {
  const correlationId = uuidv4();
  yield put(
    SideLoadedDataMappingAction.update(
      {
        id: templateId,
        name: templateName,
        entries,
        // Sent so the template's columns cannot outlive the entries mapping them.
        sourceColumns,
      },
      { ...DEFAULT_ACTION_OPTIONS, correlationId },
    ),
  );
  const { success } = yield call(
    takeOwnResult,
    correlationId,
    SIDELOADED_DATA_MAPPING.UPDATE.SUCCESS,
    SIDELOADED_DATA_MAPPING.UPDATE.FAILURE,
  );

  return Boolean(success);
}

// Dispatch an offline create or update and wait for its own result, keeping the
// server's reason when it is refused.
function* putAndAwait(actionCreator, payload, subAction) {
  const correlationId = uuidv4();
  yield put(
    actionCreator(payload, { ...DEFAULT_ACTION_OPTIONS, correlationId }),
  );
  const { failure } = yield call(
    takeOwnResult,
    correlationId,
    subAction.SUCCESS,
    subAction.FAILURE,
  );
  return failure
    ? getErrorMessage(
        failure.payload?.response,
        "The column template could not be saved.",
      )
    : null;
}

// Waited on so a refusal - one that would leave a data set unreadable under the
// new mapping - reaches the dialog instead of being lost once it has closed.
function* saveMapping(action) {
  const { dataSetId, mappingId, name, entries, sourceColumns } = action;

  const errorMessage = mappingId
    ? (yield* putAndAwait(
        SideLoadedDataMappingAction.update,
        { id: mappingId, name, entries, sourceColumns },
        SIDELOADED_DATA_MAPPING.UPDATE,
      )) ||
      (yield* putAndAwait(
        SideLoadedDataSetAction.update,
        { id: dataSetId, mappingId },
        SIDELOADED_BUSINESS_SET.UPDATE,
      ))
    : // The create response links the data set server-side.
      yield* putAndAwait(
        SideLoadedDataMappingAction.create,
        { id: uuidv4(), name, entries, datasetId: dataSetId },
        SIDELOADED_DATA_MAPPING.CREATE,
      );

  if (errorMessage) {
    yield put(saveSideLoadedDataSetMappingFailure(errorMessage));
    return;
  }

  yield put(saveSideLoadedDataSetMappingSuccess());
  // The server re-indexes the records under the new mapping, and pusher
  // excludes the socket that asked.
  yield* refreshRecords();
}

function* uploadDataSet(action) {
  const {
    name,
    templateId,
    templateName,
    hasTemplateEdits,
    entries,
    sourceColumns,
    meta: { file },
  } = action;

  let mappingId = templateId || null;
  if (mappingId && hasTemplateEdits) {
    const updated = yield* updateTemplate({
      templateId: mappingId,
      templateName,
      entries,
      sourceColumns,
    });
    if (!updated) {
      const message = "The column template could not be saved.";
      yield put(uploadSideLoadedDataSetFailure(message));
      toast.error(`${name}: ${message}`);
      return;
    }
  } else if (!mappingId && entries?.length) {
    mappingId = yield* createTemplate({ templateName, entries, sourceColumns });
    if (!mappingId) {
      const message = "The column template could not be saved.";
      yield put(uploadSideLoadedDataSetFailure(message));
      toast.error(`${name}: ${message}`);
      return;
    }
  }

  const { auth } = yield select();
  const channel = yield call(
    createUploadFileChannel,
    UPLOAD_URL,
    file,
    mappingId ? { name, mapping_id: mappingId } : { name },
    auth,
    "POST",
    "source_data",
  );

  while (true) {
    const {
      err,
      status_code: statusCode,
      response,
      success,
    } = yield take(channel);

    if (err) {
      if (statusCode === 401) {
        yield call(refreshJWTAndRetry, action);
        return;
      }
      const errorMessage = getErrorMessage(
        response,
        statusCode === 403
          ? "You do not have permission to upload data sets."
          : "The file could not be uploaded. Please try again.",
      );
      yield put(uploadSideLoadedDataSetFailure(errorMessage));
      // The modal stays open showing this same message above the dropzone.
      toast.error(`${name}: ${errorMessage}`);
      return;
    }

    if (success) {
      yield put(uploadSideLoadedDataSetSuccess());
      // The CSV is parsed within the request, so both the data set and its
      // records exist by now. Pusher excludes the socket that uploaded, so this
      // is the only thing that brings them in for the uploader.
      yield* refreshDataSets();
      yield* refreshRecords();
      toast.success(
        `${response.name}: ${response.record_count.toLocaleString()} records loaded.`,
      );
      yield* refreshMappings();
      closeModal(); // Mapping already happened, before the file was sent.
      return;
    }
  }
}

function* refreshRecords() {
  const state = yield select();
  yield put(
    SideLoadedBusinessAction.requestChanges({
      changesSince: state.sideLoadedBusinesses.lastModifiedTimestamp,
    }),
  );
}

function* refreshDataSets() {
  const state = yield select();
  yield put(
    SideLoadedDataSetAction.requestChanges({
      changesSince: state.sideLoadedDataSets.lastModifiedTimestamp,
    }),
  );
}

function* refreshMappings() {
  const state = yield select();
  yield put(
    SideLoadedDataMappingAction.requestChanges({
      changesSince: state.sideLoadedDataMappings.lastModifiedTimestamp,
    }),
  );
}

function* refreshFromSocket() {
  yield* refreshRecords();
  yield* refreshDataSets();
  yield* refreshMappings();
}

export default function* sideLoadedDataSetsSaga() {
  yield takeEvery(SIDELOADED_BUSINESS_SET.UPLOAD.REQUEST, uploadDataSet);
  yield takeEvery(SIDELOADED_BUSINESS_SET.SAVE_MAPPING.REQUEST, saveMapping);
  yield takeEvery(
    SIDELOADED_BUSINESS_SET.REFRESH.FROM_SOCKET,
    refreshFromSocket,
  );
  // Pusher excludes the socket that made the request, so the deleting client
  // never hears about its own delete - and nothing local drops the deleted data
  // set's records, which live in their own slice.
  yield takeEvery(SIDELOADED_BUSINESS_SET.DELETE.SUCCESS, refreshRecords);
}
